import qs from "qs";
import React, { useContext, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { isAuthenticated } from "../../services/AuthService";
import { Api } from "../../services/api";
import { SocketContext } from "../../utils/socket";

function AlertModal() {
  const socket = useContext(SocketContext);
  const { eventId: paramEventId } = useParams();
  const locale = (qs.parse(window.location.search) || {}).locale || "en";
  const api = useMemo(() => new Api(), []);
  const toastContainerId = {
    containerId: "alerts",
  };

  useEffect(() => {
    const handleAlert = (msg) => {
      if (!msg.isPrivate) {
        toast.dismiss();

        const titleRaw =
          msg.title?.[locale] || msg.title?.en || msg.title?.it || "";
        const titleLower = titleRaw.toLowerCase();

        const isLiveAlert = titleLower.includes("live on ecommerceweek");
        const isLiveEndedAlert = titleLower.includes("live ended");

        const liveVideoSection = document.getElementById("live-video-section");
        const isAlreadyLive =
          liveVideoSection &&
          liveVideoSection.getAttribute("data-is-live") === "true";

        if (isLiveAlert && isAlreadyLive) {
          // Already on live, ignore alert
          return;
        }

        if (isLiveEndedAlert && !isAlreadyLive) {
          // Not on live mode, ignore
          return;
        }

        let toastMessage = titleRaw;
        if (isLiveAlert) {
          toastMessage =
            "È appena iniziata una sessione live! La pagina verrà ricaricata a breve";
        } else if (isLiveEndedAlert) {
          toastMessage =
            "La sessione live è terminata. La pagina verrà ricaricata a breve";
        }

        if (!isLiveAlert && !isLiveEndedAlert) {
          toast.success(toastMessage, {
            ...toastContainerId,
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "light",
          });
        }

        if (isLiveAlert || isLiveEndedAlert) {
          // Polling mechanism
          const checkApiAndReload = async (sessionIds) => {
            try {
              const currentUser = await isAuthenticated();
              if (!currentUser || !currentUser.token) {
                // If not logged in, do nothing
                return;
              }

              // We always check the 'live' status endpoint to see if the state has changed
              const url = `/api/sessionvideos?where[session][in]=${sessionIds}&where[status][equals]=live&where[isPublished][equals]=true`;
              const response = await api.getWithAuth(url, currentUser);
              
              if (!response.ok) {
                console.error("API returned non-OK status:", response.status);
                setTimeout(() => checkApiAndReload(sessionIds), 30000);
                return;
              }

              const json = await response.json();

              const hasLiveVideo = json.docs && json.docs.length > 0;
              let shouldReload = false;

              if (isLiveAlert && hasLiveVideo) {
                shouldReload = true; // State changed to Live
              } else if (isLiveEndedAlert && !hasLiveVideo) {
                shouldReload = true; // State changed from Live to something else (VOD/Planned)
              }

              if (shouldReload) {
                toast.success(toastMessage, {
                  ...toastContainerId,
                  autoClose: 4000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true,
                  theme: "light",
                });
                setTimeout(() => {
                  window.dispatchEvent(new Event("reload-live-data"));
                  window.location.reload();
                }, 3000); // delay after confirming
              } else {
                // Try again in 30 seconds
                setTimeout(() => checkApiAndReload(sessionIds), 30000);
              }
            } catch (error) {
              console.error("Error polling API for live status", error);
              setTimeout(() => checkApiAndReload(sessionIds), 30000);
            }
          };

          const startPolling = async () => {
            const currentUser = await isAuthenticated();
            if (!currentUser || !currentUser.token) return;

            const eventIdToUse = paramEventId || "6989a9e9c521905997f219af";
            const sessRes = await api.getWithAuth(
              `/api/sessions?where[event][equals]=${eventIdToUse}&limit=100`,
              currentUser
            );
            const sessJson = await sessRes.json();
            const sessionIds = sessJson.docs?.map((s) => s.id).join(",");

            if (sessionIds) {
              checkApiAndReload(sessionIds);
            }
          };

          // Start polling
          startPolling();
        }
      }
    };

    socket.on("alerts", handleAlert);

    return () => {
      socket.off("alerts", handleAlert);
    };
  }, [socket, locale, toastContainerId.containerId, api, paramEventId]);

  return (
    <React.Fragment>
      <ToastContainer
        containerId={toastContainerId.containerId}
        enableMultiContainer
        position="bottom-center"
        autoClose={10000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        theme="light"
      />
    </React.Fragment>
  );
}

export default AlertModal;
